import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTheme } from "../../contexts/ThemeContext";
import { Link } from "react-router-dom";
import {
  useCreateGymMutation,
  useEditGymMutation,
  useGetGymByIdQuery,
} from "../../services/gym/gym.rtk.api";
import { useGetOrganisationsQuery, useGetProfileQuery } from "../../services";
import { GymFormData, GymFormErrors } from "../../types/gym";
import { useAppSelector } from "../../redux/hooks";
import { RootState } from "../../redux/store";
import { toast } from "../../core/toast";
import { State, Country } from "country-state-city";
import { adminRoutes } from "../../routers/AdminRouters";
import { CiGps } from "react-icons/ci";
import { LicensesType } from "../../types/subscription/subscriptionScreen/subscriptionScreen.type";
import PhoneInputField from "../../components/PhoneInputField";
import {
  selectOrganisationId,
  setSelectedOrganisation,
} from "../../redux/organisationReducer";
import { useAppDispatch } from "../../redux/hooks";
import { position } from "html2canvas/dist/types/css/property-descriptors/position";

const GymCreate: React.FC = () => {
  const navigate = useNavigate();
  const { gymid } = useParams();
  const { theme } = useTheme();
  const isEditMode = !!gymid;

  const [createGym, { isLoading: isCreating }] = useCreateGymMutation();
  const [editGym, { isLoading: isUpdating }] = useEditGymMutation();

  // Fetch gym by id via RTK if in edit mode
  const {
    data: gymData,
    isLoading: isGymLoading,
    isFetching,
  } = useGetGymByIdQuery(gymid as string, { skip: !isEditMode });

  const [searchParams] = useSearchParams();
  const orgIdParam = searchParams.get("orgId");

  const [formData, setFormData] = useState<GymFormData>({
    name: "",
    organisation: orgIdParam || "",
    state: "",
    city: "",
    location: "",
    contact: {
      name: "",
      email: "",
      phone: "",
    },
    screens: {
      premium: 0,
      basic: 0,
    },
    allowGuestAccess: true,
    geoLocation: {
      latitude: 0,
      longitude: 0,
    },
    locationAccess: false,
    locationRangeInMeters: 0,
  });

  const [gymLocation, setGymLocation] = useState<{
    latitude: Number;
    longitude: Number;
  }>({
    latitude: 0,
    longitude: 0,
  });
  const [locationError, setLocationError] = useState<string>("");
  const [accuracy, setAccuracy] = useState<number | null>(null);
  const [isFetchingLocation, setIsFetchingLocation] = useState<boolean>(false);
  const organisationId = useAppSelector(selectOrganisationId);
  const dispatch = useAppDispatch();

  // Set default organization from Redux and sync when Redux changes
  useEffect(() => {
    if (
      !isEditMode &&
      !orgIdParam &&
      organisationId &&
      formData.organisation !== organisationId
    ) {
      setFormData((prev) => ({ ...prev, organisation: organisationId }));
    }
  }, [organisationId, isEditMode, orgIdParam, formData.organisation]);
  const [initialData, setInitialData] = useState<GymFormData | null>(null);

  // Prefill form when data arrives in edit mode
  // Prefill form when data arrives in edit mode
  // Prefill form when data arrives in edit mode
  useEffect(() => {
    if (isEditMode && gymData) {
      const initialValues = {
        name: gymData.name ?? "",
        organisation:
          typeof gymData.organisation === "object"
            ? ((gymData.organisation as any)._id ?? "")
            : (gymData.organisation ?? ""),
        state: gymData.state ?? "",
        city: gymData.city ?? "",
        location: gymData.location ?? "",
        contact: {
          name: gymData.contact?.name ?? (gymData as any).contactPerson ?? "",
          email: gymData.contact?.email ?? (gymData as any).email ?? "",
          phone: String(gymData.contact?.phone ?? (gymData as any).phone ?? ""),
        },
        allowGuestAccess: gymData.allowGuestAccess ?? true,
        geoLocation: {
          latitude:
            gymData.geoLocation?.latitude != null
              ? gymData.geoLocation.latitude
              : 0,
          longitude:
            gymData.geoLocation?.longitude != null
              ? gymData.geoLocation.longitude
              : 0,
        },
        // 👇 YOU MUST ADD THESE TWO LINES:
        locationAccess: gymData.locationAccess ?? false,
        locationRangeInMeters: gymData.locationRangeInMeters ?? 0,
      };
      setFormData(initialValues);
      setInitialData(JSON.parse(JSON.stringify(initialValues)));
      setStateSearch(gymData.state ?? "");
    }
  }, [gymData, isEditMode]);

  useEffect(() => {}, []);

  const handleFetchLocation = async () => {
    setIsFetchingLocation(true); // Start loading
    setLocationError("");

    try {
      if (!navigator.geolocation) {
        setLocationError("Geolocation is not supported by your browser");
        setIsFetchingLocation(false);
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          setGymLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
          setFormData((prev) => ({
            ...prev,
            geoLocation: {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
            },
          }));
          setAccuracy(position.coords.accuracy);
          setIsFetchingLocation(false); // Stop loading on success
        },
        (error) => {
          setLocationError("Unable to retrieve location");
          console.error("geolocation error", error);
          setIsFetchingLocation(false); // Stop loading on error
        },
        { enableHighAccuracy: true, timeout: 10000 }, // Added timeout for better UX
      );
    } catch (error) {
      console.error("geolocation error", error);
      setIsFetchingLocation(false); // Stop loading on catch
    }
  };

  const [errors, setErrors] = useState<GymFormErrors>({});

  // simple email regex for inline validation
  const emailRegex = /^\S+@\S+\.\S+$/;

  // fetch organizations
  const {
    data: organisationsData,
    isLoading: isOrgLoading,
    isError: isOrgError,
  } = useGetOrganisationsQuery();

  // Fetch user profile to determine role
  const { data: profileResp } = useGetProfileQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  // Get user profile and role
  const profile = profileResp?.result ?? profileResp;
  const rawRole =
    (profile as any)?.role ??
    (profile as any)?.data?.role ??
    (profile as any)?.user?.role;

  // Check if user is SuperAdmin
  const isSuperAdmin =
    typeof rawRole === "string" &&
    rawRole.toLowerCase().replace(/[\s_-]+/g, "") === "superadmin";

  // Get all organizations from API
  const allOrganisations: Array<any> =
    (organisationsData as any)?.data ?? (organisationsData as any) ?? [];

  // Get assigned organizations for non-SuperAdmin users
  const assignedOrganisations: Array<any> =
    (profile as any)?.assignedOrganisations ?? [];

  const organisationOptions: Array<any> = isSuperAdmin
    ? allOrganisations
    : assignedOrganisations;

  // Get all states from all countries
  const allStates = Country.getAllCountries().flatMap((country) =>
    State.getStatesOfCountry(country.isoCode).map((state) => ({
      name: state.name,
      countryCode: country.isoCode,
      stateCode: state.isoCode,
      countryName: country.name,
    })),
  );

  // State search
  const [stateSearch, setStateSearch] = useState("");
  const [showStateDropdown, setShowStateDropdown] = useState(false);
  const stateRef = useRef<HTMLDivElement>(null);

  const filteredStates = allStates.filter((s) =>
    s.name.toLowerCase().includes(stateSearch.toLowerCase()),
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        stateRef.current &&
        !stateRef.current.contains(event.target as Node)
      ) {
        setShowStateDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;

    if (name.startsWith("contact.")) {
      const key = name.split(".")[1] as keyof GymFormData["contact"];

      // sanitize phone: keep only digits and +
      if (key === "phone") {
        const cleaned = value.replace(/[^\d+]/g, "");
        setFormData((prev) => ({
          ...prev,
          contact: { ...prev.contact, phone: cleaned },
        }));
      } else {
        setFormData((prev) => ({
          ...prev,
          contact: { ...prev.contact, [key]: value },
        }));
      }

      // clear any existing field error when user changes value
      if (errors.contact?.[key]) {
        setErrors((prev) => ({
          ...prev,
          contact: { ...(prev.contact ?? {}), [key]: undefined },
        }));
      }
      return;
    }

    if (name.startsWith("geoLocation.")) {
      const key = name.split(".")[1] as keyof NonNullable<
        GymFormData["geoLocation"]
      >;
      setFormData((prev) => ({
        ...prev,
        geoLocation: {
          ...(prev.geoLocation ?? {}),
          [key]: value,
        },
      }));

      // clear existing error
      if (errors.geoLocation?.[key]) {
        setErrors((prev) => ({
          ...prev,
          geoLocation: { ...(prev.geoLocation ?? {}), [key]: undefined },
        }));
      }
      return;
    }

    if (name.startsWith("screens.")) {
      const key = name.split(".")[1] as keyof NonNullable<
        GymFormData["screens"]
      >;
      setFormData((prev) => ({
        ...prev,
        screens: {
          ...prev.screens,
          [key]: parseInt(value) || 0,
        },
      }));
      return;
    }

    // For state selection, set both state name and code if needed
    if (name === "state") {
      setStateSearch(value);
      setShowStateDropdown(true);
      setFormData((prev) => ({
        ...prev,
        state: value,
      }));
      if (errors.state) {
        setErrors((prev) => ({ ...prev, state: undefined }));
      }
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Reverse sync: Update global organization when selection changes
    if (name === "organisation" && value && value !== organisationId) {
      dispatch(setSelectedOrganisation(value));
    }

    if ((errors as any)[name]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  // inline email validation on blur
  const handleEmailBlur = () => {
    const email = formData.contact.email.trim();
    setErrors((prev) => ({
      ...prev,
      contact: {
        ...(prev.contact ?? {}),
        email:
          email && !emailRegex.test(email) ? "Email is invalid" : undefined,
      },
    }));
  };

  const incrementScreens = (type: "premium" | "basic") => {
    setFormData((prev) => ({
      ...prev,
      screens: {
        ...prev.screens,
        [type]: (prev.screens?.[type] ?? 0) + 1,
      },
    }));
  };

  const decrementScreens = (type: "premium" | "basic") => {
    setFormData((prev) => ({
      ...prev,
      screens: {
        ...prev.screens,
        [type]: Math.max(0, (prev.screens?.[type] ?? 0) - 1),
      },
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: GymFormErrors = {};

    if (!String(formData.name || "").trim())
      newErrors.name = "Gym name is required";
    if (!String(formData.organisation || "").trim())
      newErrors.organisation = "Organisation is required";
    if (!String(formData.state || "").trim())
      newErrors.state = "State is required";
    if (!String(formData.city || "").trim())
      newErrors.city = "City is required";
    if (!String(formData.location || "").trim())
      newErrors.location = "Location is required";

    const contactErrors: GymFormErrors["contact"] = {};
    if (!String(formData.contact.name || "").trim())
      contactErrors.name = "Contact person is required";
    if (!String(formData.contact.email || "").trim())
      contactErrors.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(String(formData.contact.email || "")))
      contactErrors.email = "Email is invalid";
    if (!String(formData.contact.phone || "").trim())
      contactErrors.phone = "Phone number is required";

    if (Object.keys(contactErrors).length) newErrors.contact = contactErrors;

    if (formData.locationAccess === true) {
      const geoErrors: GymFormErrors["geoLocation"] = {};

      const lat = String(formData.geoLocation?.latitude || "").trim();
      const lng = String(formData.geoLocation?.longitude || "").trim();

      // Check Latitude (Removed lat === "0" check so 0 is allowed)
      if (!lat) {
        geoErrors.latitude = "Latitude is required";
      } else if (isNaN(Number(lat)) || Number(lat) < -90 || Number(lat) > 90) {
        geoErrors.latitude = "Invalid latitude (-90 to 90)";
      }

      // Check Longitude (Removed lng === "0" check so 0 is allowed)
      if (!lng) {
        geoErrors.longitude = "Longitude is required";
      } else if (
        isNaN(Number(lng)) ||
        Number(lng) < -180 ||
        Number(lng) > 180
      ) {
        geoErrors.longitude = "Invalid longitude (-180 to 180)";
      }

      if (Object.keys(geoErrors).length > 0) {
        newErrors.geoLocation = geoErrors;
      }
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      toast.error("Please fill all mandatory fields marked with *");
    }
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      let finalGymId = gymid;
      if (isEditMode && gymid) {
        // Detect changes
        let isChanged = false;
        const dataToUpdate: any = {};

        // Compare top-level string fields
        const topLevelFields: (keyof GymFormData)[] = [
          "name",
          "organisation",
          "state",
          "city",
          "location",
        ];
        topLevelFields.forEach((field) => {
          const current = String((formData as any)[field] || "").trim();
          const initial = String((initialData as any)?.[field] || "").trim();
          if (current !== initial) {
            isChanged = true;
            dataToUpdate[field] = current;
          }
        });

        // Compare contact fields
        if (
          formData.contact.name.trim() !== initialData?.contact?.name.trim() ||
          formData.contact.email.trim() !==
            initialData?.contact?.email.trim() ||
          String(formData.contact.phone || "").replace(/[^\d+]/g, "") !==
            String(initialData?.contact?.phone || "").replace(/[^\d+]/g, "")
        ) {
          isChanged = true;
          dataToUpdate.contact = {
            name: String(formData.contact.name || "").trim(),
            email: String(formData.contact.email || "").trim(),
            phone: String(formData.contact.phone || "").trim(),
          };
        }

        if (
          formData.allowGuestAccess !== (initialData?.allowGuestAccess ?? true)
        ) {
          isChanged = true;
          dataToUpdate.allowGuestAccess = formData.allowGuestAccess;
          if (formData.allowGuestAccess === false) {
            dataToUpdate.locationAccess = false;
            dataToUpdate.locationRangeInMeters = 0;
          }
        }

        if (
          formData.geoLocation?.latitude !==
            initialData?.geoLocation?.latitude ||
          formData.geoLocation?.longitude !==
            initialData?.geoLocation?.longitude
        ) {
          isChanged = true;
          dataToUpdate.geoLocation = {
            latitude: formData.geoLocation?.latitude,
            longitude: formData.geoLocation?.longitude,
          };
        }

        // 1. Check for locationAccess change
        if (formData.locationAccess !== initialData?.locationAccess) {
          isChanged = true;
          dataToUpdate.locationAccess = formData.locationAccess;
        }

        // 2. Check for locationRangeInMeters change (Ensure both are compared as numbers)
        if (
          Number(formData.locationRangeInMeters) !==
          Number(initialData?.locationRangeInMeters)
        ) {
          isChanged = true;
          dataToUpdate.locationRangeInMeters = Number(
            formData.locationRangeInMeters,
          );
        }

        // 3. Fix the geoLocation comparison (Ensure both are compared as numbers)
        if (
          Number(formData.geoLocation?.latitude) !==
            Number(initialData?.geoLocation?.latitude) ||
          Number(formData.geoLocation?.longitude) !==
            Number(initialData?.geoLocation?.longitude)
        ) {
          isChanged = true;
          dataToUpdate.geoLocation = {
            latitude: Number(formData.geoLocation?.latitude),
            longitude: Number(formData.geoLocation?.longitude),
          };
        }

        // If nothing changed, just redirect
        if (!isChanged) {
          // toast.success("No changes detected. Redirecting...");
          navigate(adminRoutes.gymList);
          return;
        }

        await editGym({
          gymid,
          data: dataToUpdate,
        }).unwrap();
      } else {
        const res = await createGym({
          name: formData.name,
          organisation: formData.organisation,
          state: formData.state,
          city: formData.city,
          location: formData.location,
          contact: {
            name: formData.contact.name,
            email: formData.contact.email,
            phone: formData.contact.phone,
          },
          allowGuestAccess: formData.allowGuestAccess ?? false,
          ...(formData.allowGuestAccess === true && formData.geoLocation
            ? {
                geoLocation: {
                  latitude: formData.geoLocation.latitude,
                  longitude: formData.geoLocation.longitude,
                },
              }
            : {}),

          ...(formData.locationAccess
            ? {
                locationAccess: true,
                locationRangeInMeters: formData.locationRangeInMeters || 50,
              }
            : {
                locationAccess: false,
                locationRangeInMeters: 0,
              }),

          // screens: {
          // premium: formData.screens?.premium ?? 0,
          // basic: formData.screens?.basic ?? 0,
          // },

          // pass selected organization id
          organisationId: formData.organisation,
        } as any).unwrap();

        finalGymId = res?.result?._id;
      }

      if (finalGymId) {
        navigate(adminRoutes.gymAssignLicenses.replace(":gymid", finalGymId));
      } else {
        navigate(adminRoutes.gymList);
      }
    } catch (err) {
      console.error(
        isEditMode ? "Update gym failed:" : "Create gym failed:",
        err,
      );
    }
  };
  type NavbarSliceState = { isOpen: boolean };
  const isSidebarOpen = useAppSelector(
    (state: RootState) => (state.navbar as NavbarSliceState).isOpen,
  );
  const sidebarWidth = isSidebarOpen ? 256 : 64;
  const isBusy = isCreating || isUpdating || isGymLoading || isFetching;

  return (
    <div
      className={`px-4 md:px-6 lg:px-8 pt-20 pb-8 min-h-screen ${
        theme === "dark" ? "bg-black" : "bg-gray-50"
      } p-6`}
      style={{
        marginLeft: sidebarWidth,
        width: `calc(100% - ${sidebarWidth}px)`,
      }}
    >
      {/* Header */}
      <div className="mb-6">
        <h1
          className={`text-2xl font-semibold ${
            theme === "dark" ? "text-gray-300" : "text-gray-800"
          }`}
        >
          Gyms Overview
        </h1>
        <div className="flex items-center gap-2 mt-2 text-sm">
          <Link
            to={`${adminRoutes.gymList}`}
            className={theme === "dark" ? "text-gray-400" : "text-gray-600"}
          >
            All Gyms
          </Link>
          <span
            className={theme === "dark" ? "text-gray-400" : "text-gray-600"}
          >
            /
          </span>
          <span
            className={theme === "dark" ? "text-[#B6CB24]" : "text-lime-600"}
          >
            {isEditMode ? "Edit Gym" : "Create Gym"}
          </span>
        </div>
      </div>
      <p>
        {/* Accuracy: {accuracy ? `${accuracy.toFixed(2)} meters` : "Not fetched"} */}
      </p>
      <div className="flex gap-6">
        {/* Form Section */}
        <div
          className={`flex-1 ${
            theme === "dark"
              ? "bg-[#0F0F0F] border border-gray-800"
              : "bg-white border border-gray-200"
          } rounded-lg p-6`}
        >
          <div className="mb-6">
            <h2
              className={`text-base font-medium border-b pb-2 border-gray-700 ${
                theme === "dark" ? "text-white" : "text-gray-800"
              }`}
            >
              {isEditMode ? "Edit Gym" : "Create New Gym"}
            </h2>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Row 1: Gym Name and Organisation */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              {/* Gym Name */}
              <div>
                <label
                  className={`block text-xs font-normal mb-2 ${
                    theme === "dark" ? "text-[#F3F3F6]" : "text-gray-700"
                  }`}
                >
                  Gym Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter here"
                  className={`w-full px-3 py-2.5 rounded-md text-sm ${
                    errors.name
                      ? "border border-red-500"
                      : theme === "dark"
                        ? "bg-black border border-gray-800 text-white placeholder-gray-600"
                        : "bg-white border border-gray-300 text-gray-900 placeholder-gray-400"
                  } focus:outline-none focus:ring-1 focus:ring-lime-500`}
                />
                {errors.name && (
                  <p className="text-red-500 text-xs mt-1">{errors.name}</p>
                )}
              </div>

              {/* Organisation */}
              <div>
                <label
                  className={`block text-xs font-normal mb-2 ${
                    theme === "dark" ? "text-[#F3F3F6]" : "text-gray-700"
                  }`}
                >
                  Organisation <span className="text-red-500">*</span>
                </label>
                <select
                  name="organisation"
                  value={formData.organisation}
                  onChange={handleChange}
                  disabled={isOrgLoading || isOrgError}
                  className={`w-full px-3 py-2.5 rounded-md text-sm ${
                    errors.organisation
                      ? "border border-red-500"
                      : theme === "dark"
                        ? "bg-black border border-gray-800 text-white"
                        : "bg-white border border-gray-300 text-gray-900"
                  } focus:outline-none focus:ring-1 focus:ring-lime-500 appearance-none bg-no-repeat bg-right pr-8`}
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath fill='%23ADADAD' d='M5 6L0 0h10z'/%3E%3C/svg%3E")`,
                    backgroundPosition: "right 0.75rem center",
                    backgroundSize: "10px 6px",
                  }}
                >
                  <option value="" disabled>
                    {isOrgLoading
                      ? "Loading..."
                      : isOrgError
                        ? "Failed to load"
                        : "Select organisation"}
                  </option>
                  {organisationOptions.map((org: any) => {
                    const id = org.id ?? org._id;

                    const name = org.displayName || org.name || id;
                    return (
                      <option key={id} value={id}>
                        {name}
                      </option>
                    );
                  })}
                </select>
                {errors.organisation && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.organisation}
                  </p>
                )}
              </div>
            </div>

            {/* Row 2: State, City, Location */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              {/* State */}
              <div>
                <label
                  className={`block text-xs font-normal mb-2 ${
                    theme === "dark" ? "text-[#F3F3F6]" : "text-gray-700"
                  }`}
                >
                  State <span className="text-red-500">*</span>
                </label>

                <div className="relative" ref={stateRef}>
                  <input
                    type="text"
                    name="state"
                    value={stateSearch}
                    autoComplete="off"
                    onChange={handleChange}
                    onFocus={() => setShowStateDropdown(true)}
                    placeholder="Search state..."
                    className={`w-full px-3 py-2.5 rounded-md text-sm ${
                      errors.state
                        ? "border border-red-500"
                        : theme === "dark"
                          ? "bg-black border border-gray-800 text-white placeholder-gray-600"
                          : "bg-white border border-gray-300 text-gray-900 placeholder-gray-400"
                    } focus:outline-none focus:ring-1 focus:ring-lime-500`}
                  />
                  {showStateDropdown && filteredStates.length > 0 && (
                    <div
                      className={`absolute z-10 w-full mt-1 max-h-60 overflow-y-auto rounded-md shadow-lg overscroll-contain custom-scrollbar ${
                        theme === "dark"
                          ? "bg-[#1A1A1A] border border-gray-800"
                          : "bg-white border border-gray-200"
                      }`}
                    >
                      {filteredStates.map((s) => (
                        <div
                          key={s.countryCode + "-" + s.stateCode}
                          className={`px-3 py-2 text-sm cursor-pointer transition-colors ${
                            theme === "dark"
                              ? "text-gray-200 hover:bg-gray-800"
                              : "text-gray-900 hover:bg-gray-100"
                          } ${formData.state === s.name ? (theme === "dark" ? "bg-gray-800" : "bg-gray-100") : ""}`}
                          onClick={() => {
                            setFormData((prev) => ({ ...prev, state: s.name }));
                            setStateSearch(s.name);
                            setShowStateDropdown(false);
                            if (errors.state) {
                              setErrors((prev) => ({
                                ...prev,
                                state: undefined,
                              }));
                            }
                          }}
                        >
                          {s.name}{" "}
                          <span className="text-[10px] opacity-60">
                            ({s.countryName})
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                  {showStateDropdown && filteredStates.length === 0 && (
                    <div
                      className={`absolute z-10 w-full mt-1 p-3 text-xs rounded-md shadow-lg ${
                        theme === "dark"
                          ? "bg-[#1A1A1A] text-gray-400 border border-gray-800"
                          : "bg-white text-gray-500 border border-gray-200"
                      }`}
                    >
                      No results found
                    </div>
                  )}
                </div>
                {errors.state && (
                  <p className="text-red-500 text-xs mt-1">{errors.state}</p>
                )}
              </div>

              {/* City */}
              <div>
                <label
                  className={`block text-xs font-normal mb-2 ${
                    theme === "dark" ? "text-[#F3F3F6]" : "text-gray-700"
                  }`}
                >
                  City <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  placeholder="Enter here"
                  className={`w-full px-3 py-2.5 rounded-md text-sm ${
                    errors.city
                      ? "border border-red-500"
                      : theme === "dark"
                        ? "bg-black border border-gray-800 text-white placeholder-gray-600"
                        : "bg-white border border-gray-300 text-gray-900 placeholder-gray-400"
                  } focus:outline-none focus:ring-1 focus:ring-lime-500`}
                />
                {errors.city && (
                  <p className="text-red-500 text-xs mt-1">{errors.city}</p>
                )}
              </div>

              {/* Location */}
              <div>
                <label
                  className={`block text-xs font-normal mb-2 ${
                    theme === "dark" ? "text-[#F3F3F6]" : "text-gray-700"
                  }`}
                >
                  Location <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  placeholder="Enter here"
                  className={`w-full px-3 py-2.5 rounded-md text-sm ${
                    errors.location
                      ? "border border-red-500"
                      : theme === "dark"
                        ? "bg-black border border-gray-800 text-white placeholder-gray-600"
                        : "bg-white border border-gray-300 text-gray-900 placeholder-gray-400"
                  } focus:outline-none focus:ring-1 focus:ring-lime-500`}
                />
                {errors.location && (
                  <p className="text-red-500 text-xs mt-1">{errors.location}</p>
                )}
              </div>
            </div>

            {/* Row 3: Contact Person, Email, Phone Number */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              {/* Contact Person */}
              <div>
                <label
                  className={`block text-xs font-normal mb-2 ${
                    theme === "dark" ? "text-[#F3F3F6]" : "text-gray-700"
                  }`}
                >
                  Contact Person <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="contact.name"
                  value={formData.contact.name}
                  onChange={handleChange}
                  placeholder="Enter here"
                  className={`w-full px-3 py-2.5 rounded-md text-sm ${
                    errors.contact?.name
                      ? "border border-red-500"
                      : theme === "dark"
                        ? "bg-black border border-gray-800 text-white placeholder-gray-600"
                        : "bg-white border border-gray-300 text-gray-900 placeholder-gray-400"
                  } focus:outline-none focus:ring-1 focus:ring-lime-500`}
                />
                {errors.contact?.name && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.contact.name}
                  </p>
                )}
              </div>

              {/* Email */}
              <div>
                <label
                  className={`block text-xs font-normal mb-2 ${
                    theme === "dark" ? "text-[#F3F3F6]" : "text-gray-700"
                  }`}
                >
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="contact.email"
                  value={formData.contact.email}
                  onChange={handleChange}
                  onBlur={handleEmailBlur}
                  placeholder="Enter here"
                  className={`w-full px-3 py-2.5 rounded-md text-sm ${
                    errors.contact?.email
                      ? "border border-red-500"
                      : theme === "dark"
                        ? "bg-black border border-gray-800 text-white placeholder-gray-600"
                        : "bg-white border border-gray-300 text-gray-900 placeholder-gray-400"
                  } focus:outline-none focus:ring-1 focus:ring-lime-500`}
                />
                {errors.contact?.email && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.contact.email}
                  </p>
                )}
              </div>

              <PhoneInputField
                label="Phone Number"
                required
                value={formData.contact.phone}
                onChange={(value) => {
                  setFormData((prev) => ({
                    ...prev,
                    contact: { ...prev.contact, phone: value },
                  }));
                  if (errors.contact?.phone)
                    setErrors((prev) => ({
                      ...prev,
                      contact: { ...(prev.contact ?? {}), phone: undefined },
                    }));
                }}
                error={errors.contact?.phone}
                theme={theme}
              />
            </div>

            {/* Row 4: Guest Access */}
            <div
              className={`grid grid-cols-1 md:grid-cols-${formData.allowGuestAccess ? "3" : "3"} gap-4 mb-6`}
            >
              <div>
                <label
                  className={`block text-xs font-normal mb-2 ${
                    theme === "dark" ? "text-[#F3F3F6]" : "text-gray-700"
                  }`}
                >
                  Guest Access
                </label>
                <div
                  className={`flex items-center rounded-md w-full overflow-hidden ${
                    theme === "dark"
                      ? "bg-black border border-gray-800"
                      : "bg-white border border-gray-300"
                  }`}
                >
                  <select
                    name="allowsGuestAccess"
                    value={
                      formData.allowGuestAccess === true
                        ? "allowed"
                        : formData.allowGuestAccess === false
                          ? "denied"
                          : ""
                    }
                    onChange={(e) => {
                      const val = e.target.value;
                      const isAllowed = val === "allowed";
                      setFormData((prev) => ({
                        ...prev,
                        allowGuestAccess: val === "" ? undefined : isAllowed,
                      }));
                      if ((errors as any).allowGuestAccess) {
                        setErrors((prev) => ({
                          ...prev,
                          allowGuestAccess: undefined,
                        }));
                      }
                    }}
                    className={`w-full px-3 py-2.5 rounded-md text-sm ${
                      theme === "dark"
                        ? "bg-black border border-gray-800 text-white placeholder-gray-600"
                        : "bg-white border border-gray-300 text-gray-900 placeholder-gray-400"
                    } focus:outline-none focus:ring-1 focus:ring-lime-500`}
                  >
                    <option value="">Select</option>
                    <option value="allowed">Allowed</option>
                    <option value="denied">Denied</option>
                  </select>
                </div>
              </div>

              {/* Require Location Check Dropdown (Only visible if Guest Access is Allowed) */}
              {formData.allowGuestAccess === true && (
                <div>
                  <label
                    className={`block text-xs font-normal mb-2 ${
                      theme === "dark" ? "text-[#F3F3F6]" : "text-gray-700"
                    }`}
                  >
                    Require Location Check
                  </label>

                  <div
                    className={`flex items-center rounded-md w-full overflow-hidden ${
                      theme === "dark"
                        ? "bg-black border border-gray-800"
                        : "bg-white border border-gray-300"
                    }`}
                  >
                    <select
                      name="locationAccess"
                      value={
                        formData.locationAccess === true
                          ? "required"
                          : formData.locationAccess === false
                            ? "not_required"
                            : ""
                      }
                      onChange={(e) => {
                        const val = e.target.value;
                        const isRequired = val === "required";
                        setFormData((prev: any) => ({
                          ...prev,
                          locationAccess: val === "" ? undefined : isRequired,
                        }));
                      }}
                      className={`w-full px-3 py-2.5 rounded-md text-sm ${
                        theme === "dark"
                          ? "bg-black border border-gray-800 text-white placeholder-gray-600"
                          : "bg-white border border-gray-300 text-gray-900 placeholder-gray-400"
                      } focus:outline-none focus:ring-1 focus:ring-lime-500`}
                    >
                      <option value="">Select</option>
                      <option value="required">Required</option>
                      <option value="not_required">Not Required</option>
                    </select>
                  </div>
                </div>
              )}

              {/* Latitude */}
              {formData.locationAccess === true &&
                formData.allowGuestAccess === true && (
                  <div>
                    <label
                      className={`block text-xs font-normal mb-2 ${
                        theme === "dark" ? "text-[#F3F3F6]" : "text-gray-700"
                      }`}
                    >
                      Latitude <span className="text-red-500">*</span>
                    </label>

                    <div className="flex gap-2 items-center">
                      {/* GPS Button Left */}
                      <button
                        type="button"
                        onClick={handleFetchLocation}
                        className="w-10 h-10 flex items-center justify-center rounded-md bg-lime-500 text-black hover:bg-lime-400 transition-all shrink-0"
                      >
                        {isFetchingLocation ? (
                          <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
                        ) : (
                          <CiGps className="text-lg" />
                        )}
                      </button>

                      {/* Latitude Input */}
                      <input
                        type="number"
                        name="geoLocation.latitude"
                        value={formData.geoLocation?.latitude ?? ""}
                        onChange={handleChange}
                        placeholder="e.g. 40.7128"
                        className={`flex-1 px-3 py-2.5 rounded-md text-sm ${
                          errors.geoLocation?.latitude
                            ? "border border-red-500"
                            : theme === "dark"
                              ? "bg-black border border-gray-800 text-white placeholder-gray-600"
                              : "bg-white border border-gray-300 text-gray-900 placeholder-gray-400"
                        } focus:outline-none focus:ring-1 focus:ring-lime-500`}
                      />
                    </div>

                    {errors.geoLocation?.latitude && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.geoLocation.latitude}
                      </p>
                    )}
                  </div>
                )}

              {/* Longitude */}
              {formData.locationAccess === true &&
                formData.allowGuestAccess === true && (
                  <div>
                    <label
                      className={`block text-xs font-normal mb-2 ${
                        theme === "dark" ? "text-[#F3F3F6]" : "text-gray-700"
                      }`}
                    >
                      Longitude <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      name="geoLocation.longitude"
                      value={formData.geoLocation?.longitude ?? ""}
                      onChange={handleChange}
                      placeholder="e.g. -74.0060"
                      className={`w-full px-3 py-2.5 rounded-md text-sm ${
                        errors.geoLocation?.longitude
                          ? "border border-red-500"
                          : theme === "dark"
                            ? "bg-black border border-gray-800 text-white placeholder-gray-600"
                            : "bg-white border border-gray-300 text-gray-900 placeholder-gray-400"
                      } focus:outline-none focus:ring-1 focus:ring-lime-500`}
                    />
                    {errors.geoLocation?.longitude && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.geoLocation.longitude}
                      </p>
                    )}
                  </div>
                )}

              {formData.locationAccess === true &&
                formData.allowGuestAccess === true && (
                  <div className="w-full">
                    {/* Flex container keeps Label on the left, Small Button on the right */}
                    <div className="flex justify-between items-end mb-2">
                      <label
                        className={`block text-xs font-normal ${
                          theme === "dark" ? "text-[#F3F3F6]" : "text-gray-700"
                        }`}
                      >
                        Location Range (meters)
                      </label>

                      {/* THIS is how you make the button extremely small and compact */}
                    </div>

                    {/* Input stays full-width safely below */}
                    <input
                      type="number"
                      name="locationRangeInMeters"
                      value={formData.locationRangeInMeters ?? ""}
                      onChange={handleChange}
                      placeholder="Enter range"
                      className={`w-full px-3 py-2.5 rounded-md text-sm ${
                        errors.locationRangeInMeters
                          ? "border border-red-500"
                          : theme === "dark"
                            ? "bg-black border border-gray-800 text-white placeholder-gray-600"
                            : "bg-white border border-gray-300 text-gray-900 placeholder-gray-400"
                      } focus:outline-none focus:ring-1 focus:ring-lime-500`}
                    />
                    {errors.geoLocation?.longitude && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.geoLocation.longitude}
                      </p>
                    )}
                  </div>
                )}
            </div>

            {/* <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div>
                <label
                  className={`block text-xs font-normal mb-2 ${
                    theme === "dark" ? "text-[#F3F3F6]" : "text-gray-700"
                  }`}
                >
                  {LicensesType.PREMIUM}
                </label>
                <div
                  className={`flex items-center rounded-md w-full overflow-hidden ${
                    theme === "dark"
                      ? "bg-black border border-gray-800"
                      : "bg-white border border-gray-300"
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => decrementScreens("premium")}
                    className={`flex-shrink-0 px-2 h-10 w-10 flex items-center justify-center ${
                      theme === "dark" ? "hover:opacity-80" : "hover:opacity-70"
                    } transition-opacity`}
                    style={{ minWidth: 40 }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 28 28"
                      fill="none"
                    >
                      <path
                        d="M18.158 13.9998H9.10156"
                        stroke="#949494"
                        strokeWidth="1.5"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M13.6331 25.3203C19.8853 25.3203 24.9536 20.2519 24.9536 13.9997C24.9536 7.74753 19.8853 2.67914 13.6331 2.67914C7.38089 2.67914 2.3125 7.74753 2.3125 13.9997C2.3125 20.2519 7.38089 25.3203 13.6331 25.3203Z"
                        stroke="#949494"
                        strokeWidth="1.5"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>

                  <input
                    type="number"
                    name="screens.premium"
                    value={formData.screens?.premium ?? 0}
                    onChange={handleChange}
                    min="0"
                    className={`min-w-0 flex-1 px-3 py-2.5 text-sm text-center bg-transparent border-none outline-none ${
                      theme === "dark"
                        ? "text-white placeholder-gray-600"
                        : "text-gray-900 placeholder-gray-400"
                    } focus:outline-none focus:ring-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
                    style={{ minWidth: 0 }}
                  />

                  <button
                    type="button"
                    onClick={() => incrementScreens("premium")}
                    className={`flex-shrink-0 px-2 h-10 w-10 flex items-center justify-center ${
                      theme === "dark" ? "hover:opacity-80" : "hover:opacity-70"
                    } transition-opacity`}
                    style={{ minWidth: 40 }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 28 28"
                      fill="none"
                    >
                      <path
                        d="M25.6919 13.9997C25.6919 7.74753 20.6235 2.67914 14.3714 2.67914C8.11917 2.67914 3.05078 7.74753 3.05078 13.9997C3.05078 20.2518 8.11917 25.3203 14.3714 25.3203C20.6235 25.3203 25.6919 20.2518 25.6919 13.9997Z"
                        stroke="#949494"
                        strokeWidth="1.5"
                        strokeLinecap="square"
                      />
                      <path
                        d="M14.3681 9.4715V18.528M18.8963 13.9997H9.83984"
                        stroke="#949494"
                        strokeWidth="1.5"
                        strokeLinecap="square"
                      />
                    </svg>
                  </button>
                </div>
              </div>

              <div>
                <label
                  className={`block text-xs font-normal mb-2 ${
                    theme === "dark" ? "text-[#F3F3F6]" : "text-gray-700"
                  }`}
                >
                  {LicensesType.BASIC}
                </label>
                <div
                  className={`flex items-center rounded-md w-full overflow-hidden ${
                    theme === "dark"
                      ? "bg-black border border-gray-800"
                      : "bg-white border border-gray-300"
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => decrementScreens("basic")}
                    className={`flex-shrink-0 px-2 h-10 w-10 flex items-center justify-center ${
                      theme === "dark" ? "hover:opacity-80" : "hover:opacity-70"
                    } transition-opacity`}
                    style={{ minWidth: 40 }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 28 28"
                      fill="none"
                    >
                      <path
                        d="M18.158 13.9998H9.10156"
                        stroke="#949494"
                        strokeWidth="1.5"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M13.6331 25.3203C19.8853 25.3203 24.9536 20.2519 24.9536 13.9997C24.9536 7.74753 19.8853 2.67914 13.6331 2.67914C7.38089 2.67914 2.3125 7.74753 2.3125 13.9997C2.3125 20.2519 7.38089 25.3203 13.6331 25.3203Z"
                        stroke="#949494"
                        strokeWidth="1.5"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>

                  <input
                    type="number"
                    name="screens.basic"
                    value={formData.screens?.basic ?? 0}
                    onChange={handleChange}
                    min="0"
                    className={`min-w-0 flex-1 px-3 py-2.5 text-sm text-center bg-transparent rounded-md border-none outline-none ${
                      theme === "dark"
                        ? "text-white placeholder-gray-600"
                        : "text-gray-900 placeholder-gray-400"
                    } focus:outline-none focus:ring-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
                    style={{ minWidth: 0 }}
                  />

                  <button
                    type="button"
                    onClick={() => incrementScreens("basic")}
                    className={`flex-shrink-0 px-2 h-10 w-10 flex items-center justify-center ${
                      theme === "dark" ? "hover:opacity-80" : "hover:opacity-70"
                    } transition-opacity`}
                    style={{ minWidth: 40 }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 28 28"
                      fill="none"
                    >
                      <path
                        d="M25.6919 13.9997C25.6919 7.74753 20.6235 2.67914 14.3714 2.67914C8.11917 2.67914 3.05078 7.74753 3.05078 13.9997C3.05078 20.2518 8.11917 25.3203 14.3714 25.3203C20.6235 25.3203 25.6919 20.2518 25.6919 13.9997Z"
                        stroke="#949494"
                        strokeWidth="1.5"
                        strokeLinecap="square"
                      />
                      <path
                        d="M14.3681 9.4715V18.528M18.8963 13.9997H9.83984"
                        stroke="#949494"
                        strokeWidth="1.5"
                        strokeLinecap="square"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </div> */}

            <div className="flex gap-3 pt-4 border-t  border-gray-700">
              <button
                type="submit"
                disabled={isBusy}
                className={`px-8 py-1.5 rounded-md font-medium ${
                  theme === "dark"
                    ? "bg-[#2C2C2C] text-gray-200 hover:bg-[#333333]"
                    : "bg-gray-200 text-gray-800 hover:bg-gray-300"
                }`}
                style={{
                  background:
                    "radial-gradient(94.17% 100% at 52.48% 0%, rgba(198, 217, 44, 0.80) 0%, rgba(83, 91, 16, 0.80) 100%)",
                }}
              >
                {isEditMode ? (
                  isBusy ? (
                    "Updating..."
                  ) : (
                    "Update "
                  )
                ) : (
                  <span>{isBusy ? "Saving..." : "Save & Next"}</span>
                )}
              </button>
              <Link
                to={`${adminRoutes.gymList}`}
                className={`px-5 py-2 rounded-md font-medium ${
                  theme === "dark"
                    ? "bg-[#2C2C2C] text-gray-200 hover:bg-[#333333]"
                    : "bg-gray-200 text-gray-800 hover:bg-gray-300"
                }`}
              >
                Not Now
              </Link>
            </div>
          </form>
        </div>

        {/* <div
          className={`w-60 ${
            theme === "dark"
              ? "bg-[#1E1E1E] border border-gray-800"
              : "bg-white border border-gray-200"
          } rounded-lg p-5 h-fit`}
        >
          <div className="flex items-center justify-between mb-5">
            <h3
              className={`text-sm font-normal ${
                theme === "dark" ? "text-white" : "text-gray-800"
              }`}
            >
              Unassigned Screens
            </h3>
            <button
              className={`p-1 rounded ${
                theme === "dark" ? "hover:bg-gray-800" : "hover:bg-gray-100"
              }`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M2.5 12C2.5 7.52166 2.5 5.28249 3.89124 3.89124C5.28249 2.5 7.52166 2.5 12 2.5C16.4783 2.5 18.7175 2.5 20.1088 3.89124C21.5 5.28249 21.5 7.52166 21.5 12C21.5 16.4783 21.5 18.7175 20.1088 20.1088C18.7175 21.5 16.4783 21.5 12 21.5C7.52166 21.5 5.28249 21.5 3.89124 20.1088C2.5 18.7175 2.5 16.4783 2.5 12Z"
                  stroke="#F3F3F6"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M12 16V11.5"
                  stroke="#C6D92C"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M12 8.01172V8.00172"
                  stroke="#C6D92C"
                  stroke-width="1.8"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
          </div>

          <div className="relative flex justify-center mb-4">
            {theme === "dark" ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="237"
                height="138"
                viewBox="0 0 237 138"
                fill="none"
              >
                <g clip-path="url(#clip0_278_7424)">
                  <path
                    opacity="0.45"
                    d="M204.119 134.69H31.7825C14.2301 134.69 0 120.457 0 102.898V31.792C0 14.2343 14.2301 0 31.7825 0H235.902V102.898C235.902 120.457 221.672 134.69 204.119 134.69Z"
                    fill="black"
                  />
                  <path
                    d="M234.531 132.222V2.46872L3.56676 2.46872V132.222H234.531Z"
                    fill="#0A0A0A"
                  />
                  <path
                    d="M234.53 2.46873L236.999 0H1.09766L3.56565 2.46873H234.53Z"
                    fill="url(#paint0_linear_278_7424)"
                  />
                  <path
                    d="M234.531 2.46873V132.222L237 134.69V0L234.531 2.46873Z"
                    fill="url(#paint1_linear_278_7424)"
                  />
                  <path
                    d="M234.53 132.222H3.56565L1.09766 134.69V134.692H236.999V134.69L234.53 132.222Z"
                    fill="url(#paint2_linear_278_7424)"
                  />
                  <path
                    d="M3.56565 2.46873L1.09766 0V134.69L3.56565 132.222V2.46873Z"
                    fill="url(#paint3_linear_278_7424)"
                  />
                  <path
                    d="M126.087 138H112.012C110.184 138 108.703 136.519 108.703 134.69H129.394C129.394 136.519 127.913 138 126.085 138H126.087Z"
                    fill="url(#paint4_linear_278_7424)"
                  />
                </g>
                <defs>
                  <linearGradient
                    id="paint0_linear_278_7424"
                    x1="119.049"
                    y1="2.49501"
                    x2="119.049"
                    y2="0.0425429"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop />
                    <stop offset="1" stop-color="#5C5C5C" />
                  </linearGradient>
                  <linearGradient
                    id="paint1_linear_278_7424"
                    x1="234.531"
                    y1="67.3452"
                    x2="237"
                    y2="67.3452"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop />
                    <stop offset="1" stop-color="#5C5C5C" />
                  </linearGradient>
                  <linearGradient
                    id="paint2_linear_278_7424"
                    x1="119.049"
                    y1="134.692"
                    x2="119.049"
                    y2="132.379"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stop-color="#5C5C5C" />
                    <stop offset="1" />
                  </linearGradient>
                  <linearGradient
                    id="paint3_linear_278_7424"
                    x1="1.09766"
                    y1="67.3452"
                    x2="3.56565"
                    y2="67.3452"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stop-color="#5C5C5C" />
                    <stop offset="1" />
                  </linearGradient>
                  <linearGradient
                    id="paint4_linear_278_7424"
                    x1="119.049"
                    y1="138"
                    x2="119.049"
                    y2="134.692"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stop-color="#5C5C5C" />
                    <stop offset="1" />
                  </linearGradient>
                  <clipPath id="clip0_278_7424">
                    <rect width="237" height="138" fill="white" />
                  </clipPath>
                </defs>
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="237"
                height="138"
                viewBox="0 0 237 138"
                fill="none"
              >
                <rect
                  x="1"
                  y="1"
                  width="235"
                  height="132"
                  rx="4"
                  fill="#FFFFFF"
                  stroke="#2C2C2C"
                  strokeWidth="2"
                />
                <rect
                  x="110"
                  y="134"
                  width="17"
                  height="3"
                  rx="1.5"
                  fill="#5C5C5C"
                />
              </svg>
            )}

            <div className="absolute flex gap-4" style={{ top: "38px" }}>
              <div className="flex flex-col items-center">
                <div className="w-10 h-10 rounded-full border-2 border-[#B6CB24] bg-[#B6CB24]/10 flex items-center justify-center">
                  <span
                    className={`text-sm font-semibold ${
                      theme === "dark" ? "text-white" : "text-gray-900"
                    }`}
                  >
                    10
                  </span>
                </div>
                <span
                  className={`text-[10px] mt-1 ${
                    theme === "dark" ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  {LicensesType.PREMIUM}
                </span>
              </div>

              <div className="flex flex-col items-center">
                <div
                  className={`w-10 h-10 rounded-full border-2 flex items-center justify-center ${
                    theme === "dark"
                      ? "border-gray-600 bg-gray-800"
                      : "border-gray-400 bg-gray-200"
                  }`}
                >
                  <span
                    className={`text-sm font-semibold ${
                      theme === "dark" ? "text-white" : "text-gray-900"
                    }`}
                  >
                    15
                  </span>
                </div>
                <span
                  className={`text-[10px] mt-1 ${
                    theme === "dark" ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  {LicensesType.BASIC}
                </span>
              </div>
            </div>
          </div>
        </div> */}
      </div>
      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
            width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
            background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
            background: ${theme === "dark" ? "#333" : "#ddd"};
            border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background: #B6CB24;
        }
      `}</style>
    </div>
  );
};

export default GymCreate;
